import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import Camera from "components/Camera/Camera"
import { ResponsiveDialog } from "components/ResponsiveDialog/ResponsiveDialog"
import { TakeFotoBox } from "components/TakeFotoBox/TakeFotoBox"
import { Device, DeviceConsumable, DeviceSetup, File } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { isDeviceAux, isDeviceConsumable, isDeviceEut, isDeviceTe } from "store/typeGuards"
import { AddConsumableCard } from "./AddConsumableCard"
import { AddEutCard } from "./AddEutCard"

type ScanDialogProps = {
  open: boolean
  onClose: () => void
  deviceSetup: DeviceSetup[]
  setDeviceSetup: (arg: DeviceSetup[]) => void
  projectDevices: Device[]
  onScanned?: (barcode: string) => void
  initialBarcode?: string
}

export const ScanDeviceDialog = (props: ScanDialogProps) => {
  const { open, onClose, deviceSetup, setDeviceSetup, projectDevices, onScanned, initialBarcode } =
    props

  const { projectID } = useParams()
  const projectsState = useStoreState((state) => state.projects)
  const testEquipmentState = useStoreState((state) => state.testEquipment)
  const updateProject = useStoreActions((actions) => actions.updateProject)

  const [newDevice, setNewDevice] = useState<Device>()
  const [newDeviceSetup, setNewDeviceSetup] = useState<DeviceSetup>()
  const [notInListDevice, setNotInListDevice] = useState(false)
  const [newNote, setNewNote] = useState<string>("")
  const [images, setImages] = useState<File[]>([])

  const { fetchTestEquipment } = useStoreActions((actions) => actions)
  useEffect(() => {
    const loadTestEquipment = async () => {
      try {
        await fetchTestEquipment({ fresh: false })
      } catch (err) {
        console.error("Error loading test equipment:", err)
      }
    }
    loadTestEquipment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Prefill dialog when opened for editing a specific barcode
  useEffect(() => {
    if (open && initialBarcode) {
      const existing = deviceSetup.find((item) => item.device.barcode === initialBarcode)
      if (existing) {
        setNotInListDevice(false)
        setNewDevice(existing.device)
        setNewDeviceSetup(existing)
        setNewNote(existing.note || "")
        setImages(existing.images || [])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBarcode])

  const handleClose = () => {
    onClose()
    setNewDevice(undefined)
    setNewDeviceSetup(undefined)
    setNotInListDevice(false)
    setImages([])
    setNewNote("")
  }

  const hasScanEvidence = (setup: DeviceSetup): boolean => {
    const hasNote = !!setup.note && setup.note.trim() !== ""
    const hasImages = !!setup.images && setup.images.length > 0
    const hasLinks = !!setup.linkedTo && setup.linkedTo.length > 0
    return hasNote || hasImages || hasLinks
  }

  const isAlreadyScanned = (barcode: string) => {
    const existing = deviceSetup.find((item) => item.device.barcode === barcode)
    if (existing) return hasScanEvidence(existing)
    const linked = deviceSetup
      .flatMap((ds) => ds.linkedTo ?? [])
      .find((link) => link?.device.barcode === barcode)?.device
    if (linked) return true
    return false
  }

  const handleScan = (qrCode: string) => {
    if (isAlreadyScanned(qrCode)) {
      enqueueSnackbar("Bereits hinzugefügt!", {
        variant: "info",
      })
      return null
    }
    const existing = deviceSetup.find((item) => item.device.barcode === qrCode)
    if (existing) {
      // Allow editing an existing placeholder entry without evidence
      setNotInListDevice(false)
      setNewDevice(existing.device)
      setNewDeviceSetup(existing)
      setNewNote(existing.note || "")
      setImages(existing.images || [])
      return
    }
    const newDev = projectDevices.find((item) => item.barcode === qrCode)
    if (newDev) {
      setNotInListDevice(false)
      setNewDevice(newDev)
      setNewDeviceSetup({
        device: newDev,
        note: "",
        images: [],
        linkedTo: [],
      })
    } else {
      const notInListDev = testEquipmentState.find((item) => item.barcode === qrCode)
      if (notInListDev) {
        setNotInListDevice(true)
        setNewDevice(notInListDev)
      } else {
        enqueueSnackbar("Der QR kann nicht gefunden werden!", {
          variant: "warning",
        })
      }
    }
  }

  const handleSave = () => {
    if (newDevice) {
      const existingIndex = deviceSetup.findIndex(
        (item) => item.device.barcode === newDevice.barcode
      )
      const existingEntry = existingIndex >= 0 ? deviceSetup[existingIndex] : undefined
      const mergedLinkedTo = (newDeviceSetup?.linkedTo ?? existingEntry?.linkedTo ?? [])
        .filter((link): link is NonNullable<typeof link> => !!link?.device)
        .map((link) => ({
          device: link.device,
          note: link.note ?? "",
        }))

      const updatedEntry: DeviceSetup = {
        device: newDevice,
        note: newNote,
        images: images,
        linkedTo: mergedLinkedTo,
      }
      if (existingIndex >= 0) {
        const updated = [...deviceSetup]
        updated[existingIndex] = updatedEntry
        setDeviceSetup(updated)
      } else {
        setDeviceSetup([...deviceSetup, updatedEntry])
      }
      if (onScanned) onScanned(newDevice.barcode)

      if (notInListDevice) {
        const project = projectsState.find((project) => projectID === project?.id)
        if (project) {
          updateProject({
            ...project,
            devices: [...project.devices, newDevice],
          })
        }
      }
    }
    handleClose()
  }

  return (
    <ResponsiveDialog open={open} onClose={handleClose}>
      <DialogTitle>Device hinzufügen</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {!newDevice && (
          <>
            <Camera label="Scan QR-Code" onQRCodeScan={handleScan} />

            <Box
              component="form"
              sx={{ display: "flex", gap: 1 }}
              onSubmit={(event) => {
                event.preventDefault()
                const formData = new FormData(event.currentTarget)
                const qrCode = formData.get("QRCode") as string
                handleScan(qrCode)
              }}
            >
              <TextField
                label="Device ID / Equipment Nr."
                name="QRCode"
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                placeholder="Barcode oder Gerätenummer"
              />
              <StyledButton buttonType="custom" customText="OK" type="submit" variant="contained" />
            </Box>
          </>
        )}

        {/* ALERT IF TE or CONSUMABLE NOT IN DEVICELIST */}
        {notInListDevice && newDevice && (
          <Alert severity="warning">
            Das gescannte Device ist KEIN geplanter Bestandteil der Prüfung. Soll es dennoch
            hinzugefügt werden?
          </Alert>
        )}

        {newDevice && isDeviceAux(newDevice) && (
          <Typography>
            {newDevice.barcode}: {newDevice.name}
          </Typography>
        )}

        {/* ADD EUT */}
        {newDeviceSetup && newDevice && isDeviceEut(newDevice) && (
          <Box sx={{ height: "100%" }}>
            <AddEutCard
              newDevice={newDevice}
              newDeviceSetup={newDeviceSetup}
              setNewDeviceSetup={setNewDeviceSetup}
              projectDevices={projectDevices}
            />
          </Box>
        )}

        {newDevice && isDeviceTe(newDevice) && (
          <Typography>
            {newDevice.barcode}: {newDevice.name}
          </Typography>
        )}

        {/* ADD CONSUMABLE */}
        {newDeviceSetup && newDevice && isDeviceConsumable(newDevice) && (
          <Box sx={{ height: "100%" }}>
            <AddConsumableCard
              newDevice={newDevice as DeviceConsumable}
              setNewDevice={setNewDevice}
            />
          </Box>
        )}

        {/* ADD Note and Images */}
        {newDevice && newDeviceSetup && (
          <>
            <TextField
              fullWidth
              size="small"
              multiline
              label="Notiz / Anmerkung"
              value={newNote}
              onChange={(event) => setNewNote(event.currentTarget.value)}
            />

            <TakeFotoBox setImages={setImages} images={images} imageName={newDevice.barcode} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton buttonType="save" onClick={handleSave} />
      </DialogActions>
    </ResponsiveDialog>
  )
}
